import { useState, useEffect } from "react";
import { getMinDistanceKadToClient } from "./utils";
import { roundKalugaTruckQuantity } from "../../utils/keramzitKalugaTruck";
import { scrollAppIntoView } from "../../utils/pageScroll";

// Функция для определения категории калькулятора (возвращает полное название)
const getCalculatorCategory = (calculatorName) => {
  if (!calculatorName) return 'Нерудные материалы'; // по умолчанию для нерудки

  // Для нерудки всегда возвращаем "Нерудные материалы"
  if (calculatorName === "Нерудные материалы") {
    return "Нерудные материалы";
  }

  return 'Нерудные материалы';
};

const useProductionsLogicNonmetallic = (
  products,
  selfPickUp,
  comparisons,
  setComparisons,
  setSelectedOffer,
  offerRef
) => {
  const [shouldScroll, setShouldScroll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isValidInput =
    products &&
    products.length > 0 &&
    products.every((product) => product.name); // Проверяем только наличие имени

  const hasValidDescriptions = comparisons.some((comparison) =>
    comparison.materials.some(
      (material) =>
        material.description &&
        material.description !== "Нет данных" &&
        material.description !== "-"
    )
  );

  useEffect(() => {
    // Игнорируем временные изменения (с флагом _isTemporary)
    const hasTemporaryChanges = products.some(product => product._isTemporary);
    if (hasTemporaryChanges) {
      return;
    }

    console.log("🔍 useProductionsLogicNonmetallic: isValidInput=", isValidInput, "products=", products);
    if (!isValidInput) {
      console.log("🔍 Сбрасываем comparisons из-за невалидного ввода (нерудка)");
      setIsLoading(false);
      setComparisons([]);
    }
  }, [products, setComparisons]);

  useEffect(() => {
    if (shouldScroll && offerRef?.current) {
      scrollAppIntoView(offerRef.current);
      setShouldScroll(false);
    }
  }, [shouldScroll, offerRef]);

  const handleSelectOffer = (comparison, entry) => {
    const selectedMaterials = entry.materialsToShow;

    // Для нерудки используем доставку категории "ner"
    let deliveryData = {};
    if (selfPickUp) {
      deliveryData = { bestMethod: "Самовывоз", numberOfTrips: 0, costPerTrip: 0, totalCost: 0 };
    } else {
      const nerDelivery = comparison.delivery?.ner?.details?.data || {};
      console.log('🚚 nerDelivery data:', nerDelivery);
      console.log('🚚 nerDelivery.bestMethod:', nerDelivery.bestMethod);

      deliveryData = {
        bestMethod: nerDelivery.bestMethod || "Самовывоз",
        numberOfTrips: nerDelivery.numberOfTrips || 0,
        costPerTrip: nerDelivery.costPerTrip || 0,
        totalCost: nerDelivery.totalCost || 0,
        bitrixId: nerDelivery.bitrixId || "",
      };
      console.log('🚚 Final deliveryData:', deliveryData);
    }

    const formattedMaterials = selectedMaterials.map((material) => {
      // Для нерудки используем специальную логику расчета
      let pricePerOne = material.pricePerOne || 0;
      let quantity = material.quantity || 1;
      let sum = entry.totalSum || (Math.ceil(material.pricePerOne * quantity) || 0);

      // Для нерудки берем общую сумму из entry
      if (material.calculator === "Нерудные материалы") {
        sum = entry.totalSum || 0;
        pricePerOne = quantity > 0 ? Math.ceil(sum / quantity) : 0;
      }

      return {
        name: material.name || "Не указано",
        pricePerOne,
        basePricePerOne: material.basePricePerOne || material.pricePerOne || 0, // Базовая цена без доставки
        quantity,
        sum,
        date: material.date || "—",
        unit: material.unit || "м³",
        bitrixId: material.bitrixId || "",
        calculator: material.calculator || "Нерудные материалы",
        description: material.description || "—",
        source: material.source || "-",
        massPerOne: material.massPerOne || 0,
        volumePerOne: material.volumePerOne || 0,
        manufacturer: material.manufacturer || "",
        category: material.category || "",
        calculatorCategory: getCalculatorCategory(material.calculator),
        numberPallets: material.numberPallets || 1,
        palletUnits: material.palletUnits || 1,
        // Добавляем реальный метод доставки для этого материала
        realDeliveryMethod: deliveryData.bestMethod || "Самовывоз",
        // Добавляем поля для поиска аналогов песка
        mkr: material.mkr || null,
        kf: material.kf || null,
        class: material.class || null,
      };
    });

    const selectedOfferData = {
      production: comparison.id,
      materials: formattedMaterials,
      delivery: {
        name: deliveryData.bestMethod || "Самовывоз",
        numberOfTrips: deliveryData.numberOfTrips || 0,
        costPerTrip: deliveryData.costPerTrip || 0,
        totalCost: deliveryData.totalCost || 0,
        bitrixId: deliveryData.bitrixId || "",
      },
      date: formattedMaterials.length > 0 ? formattedMaterials[0].date : "",
      // Добавляем информацию о производстве (адрес, время работы и т.д.)
      productionInfo: comparison.productionInfo,
      // Добавляем флаг что это нерудка
      isNonmetallic: true,
    };

    console.log("📦 selectedOfferData (Нерудка):", selectedOfferData);

    setSelectedOffer(selectedOfferData);
    setShouldScroll(true);
  };

  // Для нерудки упрощенная логика группировки
  const groupedData = comparisons.reduce((acc, comparison, comparisonIndex) => {
    // Берем только нерудные материалы
    const nonmetallicMaterials = comparison.materials.filter((material) =>
      material.calculator === "Нерудные материалы"
    );

    if (nonmetallicMaterials.length > 0) {
      const materialsByProduct = products.map((product) => {
        const matches = nonmetallicMaterials.filter((m) => m.name === product.name);
        console.log(`🔍 materialsForName для ${product.name} (нерудка):`, matches);
        return { product, matches };
      });

      // Если хотя бы для одного товара нет ни одной записи, пропускаем производство
      if (materialsByProduct.some(({ product, matches }) => {
        if (!matches.length) {
          console.warn(`⚠️ Нет нерудных материалов для ${product.name}`);
          return true;
        }
        return false;
      })) {
        return acc;
      }

      // Если есть несколько вариантов (например, песок с разными Мкр/Кф),
      // выводим их отдельными строками для этого же производства.
      const variantProductIndex = materialsByProduct.findIndex(({ matches }) => matches.length > 1);
      const variantCount = variantProductIndex >= 0 ? materialsByProduct[variantProductIndex].matches.length : 1;

      for (let variantIndex = 0; variantIndex < variantCount; variantIndex++) {
        const materialsToShow = materialsByProduct.map(({ product, matches }, idx) => {
          const selectedMaterial = idx === variantProductIndex ? matches[variantIndex] : matches[0];
          return {
            ...selectedMaterial,
            // Из Калуги керамзит отгружается только полной фурой (мешки — кратно 1 500
            // мешкам, биг-бэги — кратно 72 м3), поэтому в расчёт этого производства идёт
            // количество, кратное машине — от него считаются и сумма за товар в таблице,
            // и КП (src/utils/keramzitKalugaTruck.js). Для остальных производств
            // количество остаётся как ввёл менеджер.
            quantity: roundKalugaTruckQuantity(
              product.quantity || 1,
              comparison.production,
              selectedMaterial.category
            ),
          };
        }).filter(Boolean);

        if (!materialsToShow.length) {
          console.warn("⚠️ materialsToShow пуст для нерудки");
          continue;
        }

        const totalMaterialCost = materialsToShow.reduce((sum, mat) => sum + mat.quantity * mat.pricePerOne, 0);

        let deliveryCostForGroup = 0;
        if (!selfPickUp) {
          const nerDetails = comparison.delivery?.ner?.details;
          console.log("🔍 nerDetails:", nerDetails);
          if (nerDetails && typeof nerDetails === "object" && !("error" in nerDetails)) {
            if ("data" in nerDetails) {
              deliveryCostForGroup = nerDetails.data?.totalCost || 0;
            }
          } else {
            console.error("❌ Проблема с nerDetails:", nerDetails);
          }
        }

        const totalDeliveryCost = selfPickUp ? 0 : deliveryCostForGroup;
        const totalSum = totalMaterialCost + totalDeliveryCost;

        acc.push({
          comparison,
          material: materialsToShow[0],
          production: comparison.production,
          productionInfo: comparison.productionInfo,
          delivery: comparison.delivery,
          materialsToShow,
          allMaterials: nonmetallicMaterials,
          totalMaterialCostForDisplay: totalMaterialCost,
          totalDeliveryCost,
          totalSum,
          id: `${comparison.id}-ner-${comparisonIndex}-${variantIndex}`,
          deliveryForTooltip: { ner: { category: "Нерудные материалы", details: comparison.delivery?.ner?.details } },
          categoriesToInclude: ["ner"],
        });
      }
    }

    return acc;
  }, []);

  // Уникализация и фильтрация
  const uniqueGroupedData = [];
  const seenKeys = new Set();
  groupedData.forEach(entry => {
    const key = `${entry.production}-${entry.materialsToShow.map(m => `${m.name}-${m.description}-${m.quantity}-${m.source || "-"}-${m.mkr || "-"}-${m.kf || "-"}-${m.class || "-"}-${m.pricePerOne || 0}`).join("|")}`;
    if (!seenKeys.has(key)) {
      seenKeys.add(key);
      uniqueGroupedData.push(entry);
    } else {
      console.log(`🚫 Дубликат исключён (нерудка): ${key}`);
    }
  });

  // Фильтрация для нерудки
  const filteredGroupedData = uniqueGroupedData.filter((entry) => {
    if (!selfPickUp && entry.totalDeliveryCost === 0) {
      console.log(`⏩ Пропускаем ${entry.production}: totalDeliveryCost = 0 при !selfPickUp (нерудка)`);
      return false;
    }
    return true;
  });

  // Сортировка - для нерудки ограничиваем топ 10
  const sortedGroupedData = [...filteredGroupedData]
    .sort((a, b) => {
      // Сначала сортируем по наличию цены (товары с ценой первыми)
      const aHasPrice = a.totalMaterialCostForDisplay > 0;
      const bHasPrice = b.totalMaterialCostForDisplay > 0;

      if (aHasPrice && !bHasPrice) return -1;  // товары с ценой первыми
      if (!aHasPrice && bHasPrice) return 1;   // товары "по запросу" в конец

      // Если оба статуса одинаковые, сортируем по общей сумме
      return a.totalSum - b.totalSum;
    });

  // Автоматически выбираем дешевейшее производство при загрузке сравнения
  useEffect(() => {
    if (!comparisons || comparisons.length === 0) return;
    const cheapestEntry = sortedGroupedData.find(e => e.totalMaterialCostForDisplay > 0);
    if (cheapestEntry) {
      handleSelectOffer(cheapestEntry.comparison, cheapestEntry);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisons]);

  const minDistanceKadToClient = getMinDistanceKadToClient(comparisons);

  return {
    isValidInput,
    groupedData: sortedGroupedData,
    sortedGroupedData,
    hasValidDescriptions,
    minDistanceKadToClient,
    handleSelectOffer,
    setShouldScroll,
    shouldScroll,
    isLoading,
    setIsLoading,
  };
};

export default useProductionsLogicNonmetallic;
